import { gsap } from "gsap";
import { IDestroyOptions } from "pixi.js";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { SceneIndex } from "../../../SceneIndex";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { Hand } from "../../../common/components/Hand";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { IGameController } from "../../../game/controllers/IGameController";
import { INewsModel } from "../../../game/models/INewsModel";
import { IFocusable } from "../../../layers/IFocusable";
import { CheckOutFixUpgradeTooltip } from "../components/CheckOutFixUpgradeTooltip";
import { FixBuildingButton } from "../components/FixBuildingButton";
import { GameResourcePackage } from "../../../game/resources/GameResourcePackage";
import { CheckoutsTooltipResourcePackage } from "../../../common/resources/CheckoutsTooltipResourcePackage";

export class CheckOutFixUpgrade extends BaseScalableView<void> implements IFocusable {
    public readonly focusable: boolean = true;

    private readonly commonResources: CommonResourcesPackage;
    private readonly gameResources: GameResourcePackage;
    private readonly checkoutsTooltipResources: CheckoutsTooltipResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly gameController: IGameController;
    private readonly newsModel: INewsModel;
    private fadeScreen: FadeScreen;
    private hand: Hand;
    private tooltip: CheckOutFixUpgradeTooltip;
    private myBuildingsButton: FixBuildingButton;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        commonResources: CommonResourcesPackage,
        gameResources: GameResourcePackage,
        checkoutsTooltipResources: CheckoutsTooltipResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        canvasScaler: ICanvasScaler,
        gameController: IGameController,
        newsModel: INewsModel
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.gameResources = gameResources;
        this.checkoutsTooltipResources = checkoutsTooltipResources;
        this.soundResources = soundResources;
        this.gameController = gameController;
        this.newsModel = newsModel;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.show.bind(this));
    }

    public start(): void { }

    private build(): void {
        const width = this.canvasScaler.referenceWidth;
        const height = this.canvasScaler.referenceHeight;
        this.fadeScreen = new FadeScreen(0.1, 0, 0.75, width, height);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);
        this.hand = new Hand(this.commonResources);
        this.hand.pivot.set(this.hand.width * 0.5, this.hand.height * 0.5);
        this.hand.angle = 10;
        this.addChild(this.hand);
        this.tooltip = new CheckOutFixUpgradeTooltip(this.checkoutsTooltipResources, this.newsModel);
        this.addChild(this.tooltip);
        this.myBuildingsButton = new FixBuildingButton(this.gameResources);
        this.myBuildingsButton.onUp.add(this.hide, this);
        this.addChild(this.myBuildingsButton);
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.set(this.myBuildingsButton, { interactive: false });
        animationTimeLine.fromTo(this.hand, { x: 0, y: 0, angle: 60 }, { x: this.hand.x, y: this.hand.y, angle: this.hand.angle, duration: 0.5, ease: "power2.out" });
        animationTimeLine.fromTo(this.tooltip.scale, { x: 0, y: 0 }, { x: this.tooltip.scale.x, y: this.tooltip.scale.y, duration: 0.4, ease: "power2.out" });
        animationTimeLine.set(this.myBuildingsButton, { interactive: true });
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    protected layout(_width: number, _height: number): void {
        this.hand.scale.set(0.75);
        this.hand.position.set(200, 1388);
        this.tooltip.position.set(435, 833);
        this.myBuildingsButton.position.set(40, 1558);
    }

    private async hide(): Promise<void> {
        this.soundResources.assets_sounds_Choose_Card_mp3.play();
        this.gameController.showPopupBySceneIndex(SceneIndex.MY_BUILDINGS);
        const animationTimeLine = new gsap.core.Timeline();
        await animationTimeLine.to(this, { alpha: 0, duration: 0.5, ease: "power1.out" });
        animationTimeLine.kill();
        this.operationControls.complete()
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.myBuildingsButton.onUp.remove(this.hide, this);
        super.destroy(options);
    }
}
